import React, {useEffect, useState} from "react";

import ChartContainer from "@/components/ChartContainer";
import Filters from "@/components/Filters";
import {usePageWritable, useWritableActions} from "@/components/MainActions";
import Button from "@mui/material/Button";
import {Pagination} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SwitchLeftIcon from "@mui/icons-material/SwitchLeft";
import SwitchRightIcon from "@mui/icons-material/SwitchRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {getNestedValue, orderByDate} from "@/utils/functions";

import EmptyData from "@/components/EmptyData";

import "./index.scss";
import {useTranslation} from "react-i18next";

const DEFAULT_ITEMS_PER_PAGE = 20;

export default function CardsTable(props) {
	const {
		size,
		title,
		actions: allActions,
		configurationColumns,
		configurationExpandedColumns,
		data,
		totalItems,
		itemsPerPage,
		handlePageChanged,
		page,
		fullWidth = false,
		fullHeight = true,
		middleHeight = false,
		showModes = false,
		rowClassName,
		handleClick,
		handleEdit,
		handleDelete,
		handleDataChange,
		handleSortChange,
		defaultSortedField,
		defaultSortedOrder = "ascend",
		sortOptions = [],
		filtersData,
		breadcrumbs = false,
		forceOpened = false,
		chartData,
		rowGroupingKey,
		getChildRows,
		fixExpanded,
	} = props;

	const {t} = useTranslation();
	const writable = usePageWritable();
	const actions = useWritableActions(allActions);

	//const [fixedExpanded, setFixedExpanded] = useState(true);
	const [opened, setOpened] = useState(false);
	const [expandedRows, setExpandedRows] = useState({});
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		if (page) setCurrentPage(page);
	}, [page]);

	useEffect(() => {
		if (forceOpened) {
			setOpened(true);
		}
	}, [forceOpened]);

	const [showMode, setShowMode] = useState("table");
	const [currentSortOption, setCurrentSortOption] = useState("");
	const [currentSortOrder, setCurrentSortOrder] = useState("");

	useEffect(() => {
		if (sortOptions?.length) {
			const defaultColumn = sortOptions?.filter((c) => c.sortedByDefault)?.[0];
			if (defaultColumn) {
				setCurrentSortOption(defaultColumn.key);
				setCurrentSortOrder(defaultColumn.sortedOrderByDefault);
			}
		}
	}, [sortOptions]);

	const deleteColumn = {
		label: "",
		key: "actions",
		customRender: (item, index) => {
			return !item.emptyRow && !item.readable ? (
				<div className="actions-row-container">
					<div
						className="action clickable"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();

							const updatedData = data.filter(
								(_, rowIndex) => rowIndex !== index,
							);

							if (handleDataChange && !item.id) {
								handleDataChange(updatedData);
							}

							handleDelete(item);
						}}
					>
						<CloseIcon />
					</div>
				</div>
			) : (
				<></>
			);
		},
		// small: true,
		action: true,
		align: "end",
	};

	const editColumn = {
		label: "",
		key: "actions",
		customRender: (item) => {
			return !item.emptyRow && !item.readable ? (
				<div className="actions-row-container">
					<div
						className="action clickable"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();

							handleEdit(item);
						}}
					>
						<EditIcon />
					</div>
				</div>
			) : (
				<></>
			);
		},
		// small: true,
		action: true,
		align: "end",
	};

	const handlePageChange = (_, newPage) => {
		setCurrentPage(newPage);
		handlePageChanged(newPage);
	};

	const handleInputChange = (key, index, value) => {
		const updatedData = Array.from(data);
		if (!updatedData[index]) {
			updatedData[index] = {};
		}
		updatedData[index][key] = value;

		if (handleDataChange) {
			handleDataChange(updatedData);
		}
	};

	const columns = configurationColumns ? Array.from(configurationColumns) : [];
	const expandedColumns = configurationExpandedColumns
		? Array.from(configurationExpandedColumns)
		: [];

	if (handleEdit && writable) {
		columns.push(editColumn);
	}
	if (handleDelete && writable) {
		columns.push(deleteColumn);
	}

	const sortData = (data) => {
		const fieldToSort = !sortOptions?.length
			? defaultSortedField
			: currentSortOption;
		const orderToSort = !sortOptions?.length
			? defaultSortedOrder
			: currentSortOrder;

		const fieldType =
			columns?.filter((c) => c.key === fieldToSort)?.[0]?.type || "string";

		let sortedData = [];
		switch (fieldType) {
			case "date":
				sortedData = orderByDate(data, fieldToSort, orderToSort);
				break;
			case "string":
			default:
				sortedData = data?.sort((a, b) => {
					const valueA = getNestedValue(a, fieldToSort);
					const valueB = getNestedValue(b, fieldToSort);

					return orderToSort === "descend"
						? valueB > valueA
							? 1
							: valueA > valueB
								? -1
								: 0
						: valueA > valueB
							? 1
							: valueB > valueA
								? -1
								: 0;
				});
				break;
		}
		return sortedData;
	};

	const sortedData =
		defaultSortedField || currentSortOption ? sortData(data) : data;

	const toggleRowExpansion = (rowKey) => {
		setExpandedRows((prev) => ({
			...prev,
			[rowKey]: !prev[rowKey],
		}));
	};

	useEffect(() => {
		if (fixExpanded && sortedData?.length) {
			sortedData?.forEach((singleData) => {
				setExpandedRows((prev) => ({
					...prev,
					[singleData[rowGroupingKey]]: true,
				}));
			});
		}
	}, [fixExpanded, sortedData]);

	useEffect(() => {
		if (handleSortChange && currentSortOption && currentSortOrder) {
			handleSortChange(currentSortOption, currentSortOrder);
		}
	}, [currentSortOption, currentSortOrder]);

	return (
		<>
			{filtersData && (
				<Filters
					data={filtersData}
					opened={opened}
					setOpened={setOpened}
					forceOpened={forceOpened}
				/>
			)}
			<div
				className={
					"table-container" +
					(fullWidth ? " full-width" : "") +
					(breadcrumbs ? " breadcrumbs" : "") +
					(filtersData ? (opened ? " fixed-filters" : " filtered") : "") +
					(sortOptions?.length ? " sorted" : "") +
					(handlePageChanged ? " paginated" : "")
					// 		(middleHeight ? " middle-height" : "")
				}
			>
				{sortOptions?.length ? (
					<div className="sort-options-container">
						<div>{t("sort_by")}</div>
						<div className="options-container">
							{sortOptions.map((o, k) => {
								return (
									<div
										className={
											"option-container" +
											(currentSortOption === o.key ? " selected" : "")
										}
										key={k}
										onClick={() => {
											setCurrentSortOption(o.key);

											if (!currentSortOrder) {
												setCurrentSortOrder("ascend");
											}
										}}
									>
										{o.label}
									</div>
								);
							})}
						</div>
						<div className="vertical-line" />
						<div className="options-container">
							<div
								className={
									"option-container small" +
									(currentSortOrder === "ascend" ? " selected" : "")
								}
								onClick={() => setCurrentSortOrder("ascend")}
							>
								<ArrowDownwardIcon fontSize="small" />
							</div>
							<div
								className={
									"option-container small" +
									(currentSortOrder === "descend" ? " selected" : "")
								}
								onClick={() => setCurrentSortOrder("descend")}
							>
								<ArrowUpwardIcon fontSize="small" />
							</div>
						</div>
					</div>
				) : null}
				{title || actions ? (
					<div className="title-container d-flex align-items-center justify-content-between">
						<div className="title-label">{title || " "}</div>
						{actions?.length ? (
							<div className="title-actions-container">
								{showModes && (
									<Button
										variant="outlined"
										onClick={() => {
											setShowMode(showMode === "table" ? "chart" : "table");
										}}
										size="small"
									>
										<div className="d-flex align-items-center">
											{showMode === "table" ? (
												<SwitchLeftIcon />
											) : (
												<SwitchRightIcon />
											)}
										</div>
									</Button>
								)}
								{showMode === "table" &&
									actions.map((action, key) => {
										return (
											<Button
												variant="outlined"
												onClick={() => {
													if (action.type !== "chart") {
														action.onClick();
													}
												}}
												size="small"
											>
												<div className="d-flex align-items-center">
													{action.icon}
												</div>
											</Button>
										);
									})}
							</div>
						) : (
							""
						)}
					</div>
				) : null}
				{showMode === "table" ? (
					<>
						<div
							className={
								"table-header-container" +
								(size ? " " + size : "") +
								(configurationExpandedColumns ? " expanded" : "")
							}
						>
							{columns.map((column, key) => {
								return (
									<div
										key={key}
										className={
											"header-field" +
											(column.formField ? " form-field" : "") +
											(column.small ? " sm" : "") +
											(column.medium ? " md" : "") +
											(column.action ? " action" : "") +
											(column.align
												? " justify-content-" + column.align
												: " justify-content-center")
										}
									>
										{column.hideValue ? "" : column.label}
									</div>
								);
							})}
						</div>
						<div
							className={
								"elements-container" +
								(fullHeight ? " full-height" : "") +
								(middleHeight ? " middle-height" : "")
							}
						>
							{sortedData?.map((singleData, key) => {
								const rowKey = singleData[rowGroupingKey];
								const isExpanded = expandedRows[rowKey];
								let childRows = getChildRows ? getChildRows(singleData) : [];

								childRows = childRows?.length
									? defaultSortedField
										? childRows
												?.slice()
												.sort((a, b) =>
													defaultSortedOrder === "descend"
														? b[defaultSortedField] > a[defaultSortedField]
															? 1
															: a[defaultSortedField] > b[defaultSortedField]
																? -1
																: 0
														: a[defaultSortedField] > b[defaultSortedField]
															? 1
															: b[defaultSortedField] > a[defaultSortedField]
																? -1
																: 0,
												)
										: data
									: [];

								return (
									<React.Fragment key={"parent_" + key}>
										<div
											key={key}
											className={
												"element-container" +
												(handleClick ? " clickable" : "") +
												(rowClassName ? " " + rowClassName(singleData) : "")
											}
											onClick={() => {
												if (childRows?.length) {
													toggleRowExpansion(rowKey);
												} else {
													if (handleClick) {
														handleClick(singleData);
													}
												}
											}}
										>
											{childRows?.length ? (
												<div className="expand-icon">
													{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
												</div>
											) : (
												""
											)}
											{columns.map((column, columnKey) => {
												const currentKey = column.key;
												const nestedKeys = currentKey.split(".");

												const getValueFromNestedKey = (obj, keys) => {
													for (const key of keys) {
														if (obj?.hasOwnProperty(key)) {
															obj = obj[key];
														} else {
															return null;
														}
													}
													return obj || "---";
												};

												return (
													<div
														key={key + "_" + columnKey}
														className={
															"element-field" +
															(column.small ? " small-width" : "") +
															(column.medium ? " medium-width" : "") +
															(column.action ? " action" : "") +
															(column.align
																? " justify-content-" + column.align
																: " justify-content-center") +
															(size ? " " + size : "")
														}
													>
														{column?.editRender
															? column.editRender({
																	item: singleData,
																	onChange: (value) =>
																		handleInputChange(column.key, key, value),
																})
															: column?.customRender
																? column.customRender(singleData, key)
																: getValueFromNestedKey(singleData, nestedKeys)}
													</div>
												);
											})}
										</div>
										{isExpanded && (
											<div className="expanded-rows-container">
												<div className="table-header-container">
													{expandedColumns.map((column, key) => {
														return (
															<div
																key={key}
																className={
																	"header-field" +
																	(column.small ? " sm" : "") +
																	(column.medium ? " md" : "") +
																	(column.action ? " action" : "") +
																	(column.align
																		? " justify-content-" + column.align
																		: " justify-content-center")
																}
															>
																{column.label}
															</div>
														);
													})}
												</div>
												{childRows.map((childRow, childKey) => (
													<div
														key={"child_" + rowKey + "_" + childKey}
														className={
															"element-container child-row" +
															(handleClick ? " clickable" : "")
														}
														onClick={() => {
															if (handleClick) {
																handleClick(childRow);
															}
														}}
													>
														{expandedColumns.map((column, columnKey) => (
															<div
																key={
																	"child_" +
																	rowKey +
																	"_" +
																	childKey +
																	"_" +
																	columnKey
																}
																className={
																	"element-field" +
																	(column.small ? " small-width" : "") +
																	(column.medium ? " medium-width" : "") +
																	(column.align
																		? " justify-content-" + column.align
																		: " justify-content-center") +
																	(size ? " " + size : "")
																}
															>
																{column?.customRender
																	? column.customRender(childRow)
																	: childRow[column.key]}
															</div>
														))}
													</div>
												))}
											</div>
										)}
									</React.Fragment>
								);
							})}
							{!data?.length ? <EmptyData /> : null}
						</div>
					</>
				) : (
					<div className="charts-container d-flex align-items-center gap-2">
						<ChartContainer
							type="line"
							title={chartData?.title}
							chartItems={[
								{
									value: `${chartData?.value}`,
									label: `${chartData?.label}`,
									color: "var(--color-text)",
								},
							]}
							data={sortedData
								.reverse()
								?.filter((d) => d[`${chartData?.value}`])}
							dataKey={defaultSortedField}
							range
						/>
					</div>
				)}
			</div>
			{totalItems > (itemsPerPage || DEFAULT_ITEMS_PER_PAGE) && (
				<div className="pagination-container">
					<Pagination
						count={Math.ceil(
							totalItems / (itemsPerPage || DEFAULT_ITEMS_PER_PAGE),
						)}
						page={currentPage}
						onChange={handlePageChange}
						color="primary"
					/>
				</div>
			)}
		</>
	);
}
